import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Alert,
  Container,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  IconButton,
  Stack,
  Chip,
  Checkbox,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  CloudUploadOutlined,
  PersonAddOutlined,
  CheckCircle,
  Cancel,
  Business,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { uiPT } from "../i18n";

interface FormData {
  name: string;
  type: string;
  modality: string;
  IBAN: string;
  identityType: string;
  city: string;
  idCardType: "pdf" | "image";
  selfie?: File;
  modalityPhoto?: File;
  driverLicense?: File;
  idCardFront?: File;
  idCardBack?: File;
  idCardPdf?: File;
  caeDocument?: File;
  taxClearanceDocument?: File;
  termsAccepted: boolean;
}

interface PartnerInfo {
  partnerName: string;
  partnerCode: string;
}

const schema = yup.object({
  name: yup
    .string()
    .required("Nome é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres"),
  type: yup
    .string()
    .required("Tipo é obrigatório")
    .oneOf(["courier", "driver"], "Tipo inválido"),
  modality: yup
    .string()
    .required("Modalidade é obrigatória")
    .oneOf(["bike", "motorcycle"], "Modalidade inválida"),
  IBAN: yup
    .string()
    .required("IBAN é obrigatório")
    .transform((value) => (value ? value.replace(/\s/g, "") : value))
    .matches(
      /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/,
      "IBAN inválido"
    ),
  identityType: yup
    .string()
    .required("Tipo de identidade é obrigatório")
    .oneOf(["passport", "cc", "trc", "cplp"], "Tipo de identidade inválido"),
  city: yup
    .string()
    .required("Cidade é obrigatória")
    .min(2, "Cidade deve ter pelo menos 2 caracteres"),
  idCardType: yup
    .string()
    .required("Tipo de documento é obrigatório")
    .oneOf(["pdf", "image"], "Tipo inválido"),
  selfie: yup.mixed().required("Selfie é obrigatória"),
  modalityPhoto: yup.mixed().required("Foto da modalidade é obrigatória"),
  driverLicense: yup.mixed().when("modality", {
    is: "motorcycle",
    then: (schema) =>
      schema.required("CNH é obrigatória para modalidade motocicleta"),
    otherwise: (schema) => schema.optional(),
  }),
  idCardFront: yup.mixed().when("idCardType", {
    is: "image",
    then: (schema) => schema.required("Frente do documento é obrigatória"),
    otherwise: (schema) => schema.optional(),
  }),
  idCardBack: yup.mixed().when("idCardType", {
    is: "image",
    then: (schema) => schema.required("Verso do documento é obrigatório"),
    otherwise: (schema) => schema.optional(),
  }),
  idCardPdf: yup.mixed().when("idCardType", {
    is: "pdf",
    then: (schema) => schema.required("PDF do documento é obrigatório"),
    otherwise: (schema) => schema.optional(),
  }),
  caeDocument: yup.mixed().required('Código de atividade económica (CAE) é obrigatório'),
  taxClearanceDocument: yup.mixed().required('Certidão de Não Dívida - Autoridade Tributária é obrigatória'),
  termsAccepted: yup
    .boolean()
    .oneOf([true], "Você deve aceitar os termos de uso"),
}) as yup.ObjectSchema<FormData>;

export default function ReferralRegistrationPage() {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [partnerInfo, setPartnerInfo] = useState<PartnerInfo | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>(
    {}
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      type: "courier",
      modality: "bike",
      IBAN: "",
      identityType: "",
      city: "",
      idCardType: "image",
      termsAccepted: false,
    },
  });

  const modality = watch("modality");
  const idCardType = watch("idCardType");

  useEffect(() => {
    if (linkId) {
      fetchPartnerInfo();
    }
  }, [linkId]);

  const fetchPartnerInfo = async () => {
    try {
      setPageLoading(true);
      const response = await api.get(`/referrals/partner/${linkId}`);
      setPartnerInfo(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Link de indicação inválido");
    } finally {
      setPageLoading(false);
    }
  };

  const formatIBAN = (value: string) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const formatted = cleaned.replace(/(.{4})/g, "$1 ").trim();
    return formatted;
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Append form fields
      formData.append("name", data.name);
      formData.append("type", data.type);
      formData.append("modality", data.modality);
      formData.append("IBAN", data.IBAN.replace(/\s/g, ""));
      formData.append("identityType", data.identityType);
      formData.append("city", data.city);
      formData.append("idCardType", data.idCardType);

      // Add file fields
      if (data.selfie) formData.append("selfie", data.selfie);
      if (data.modalityPhoto)
        formData.append("modalityPhoto", data.modalityPhoto);
      if (data.driverLicense)
        formData.append("driverLicense", data.driverLicense);
      if (data.idCardFront) formData.append("idCardFront", data.idCardFront);
      if (data.idCardBack) formData.append("idCardBack", data.idCardBack);
      if (data.idCardPdf) formData.append("idCardPdf", data.idCardPdf);
      if (data.caeDocument) formData.append("caeDocument", data.caeDocument);
      if (data.taxClearanceDocument) formData.append("taxClearanceDocument", data.taxClearanceDocument);

      const response = await api.post(
        `/referrals/register/${linkId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const result = response.data;
      setSuccess(
        `Registo realizado com sucesso! Código de seguimento: ${result.data.trackingCode}`
      );
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao registrar cliente");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setValue(field, file);
        setUploadedFiles((prev) => ({
          ...prev,
          [field]: file.name,
        }));
      }
    };

  const removeFile = (field: keyof FormData) => {
    setValue(field, undefined);
    setUploadedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[field];
      return newFiles;
    });
  };

  if (pageLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !partnerInfo) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate("/")} variant="contained">
          Voltar ao Início
        </Button>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) => theme.palette.grey[100],
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={4}>
          {/* Partner Info Header */}
          {partnerInfo && (
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Business sx={{ fontSize: 32, color: "primary.main" }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Indicado por: {partnerInfo.partnerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Código do Parceiro: {partnerInfo.partnerCode}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Registration Form */}
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <PersonAddOutlined
                  sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Registo via Indicação
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Complete o formulário para se registrar através da indicação
                </Typography>
                <Typography
                  variant="body2"
                  color="info.main"
                  sx={{ mt: 1, fontWeight: 500 }}
                >
                  Nota: Não é necessário número de telefone para registos via
                  indicação
                </Typography>
              </Box>

              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ width: "100%" }}
              >
                <Stack spacing={3}>
                  {/* Personal Information */}
                  <Typography
                    variant="h6"
                    sx={{ color: "#2c3e50", fontWeight: 600 }}
                  >
                    Informações Pessoais
                  </Typography>

                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nome Completo"
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          required
                          sx={{ flex: 1, minWidth: 250 }}
                        />
                      )}
                    />
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Tipo"
                          error={!!errors.type}
                          helperText={errors.type?.message}
                          required
                          sx={{ flex: 1, minWidth: 250 }}
                        >
                          <MenuItem value="courier">
                            {uiPT.register.courier}
                          </MenuItem>
                          <MenuItem value="driver">
                            {uiPT.register.driver}
                          </MenuItem>
                        </TextField>
                      )}
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Controller
                      name="modality"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Modalidade"
                          error={!!errors.modality}
                          helperText={errors.modality?.message}
                          required
                          sx={{ flex: 1, minWidth: 250 }}
                        >
                          <MenuItem value="bike">{uiPT.register.bike}</MenuItem>
                          <MenuItem value="motorcycle">
                            {uiPT.register.motorcycle}
                          </MenuItem>
                        </TextField>
                      )}
                    />
                    <Controller
                      name="IBAN"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="IBAN"
                          placeholder="PT50 1234 5678 9012 3456 7890 1"
                          error={!!errors.IBAN}
                          helperText={errors.IBAN?.message}
                          required
                          onChange={(e) => {
                            const formatted = formatIBAN(e.target.value);
                            field.onChange(formatted);
                          }}
                          sx={{ flex: 1, minWidth: 250 }}
                        />
                      )}
                    />
                    <Controller
                      name="identityType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Tipo de Identidade"
                          error={!!errors.identityType}
                          helperText={errors.identityType?.message}
                          required
                          sx={{ flex: 1, minWidth: 250 }}
                        >
                          <MenuItem value="passport">Passaporte</MenuItem>
                          <MenuItem value="cc">Cartão de cidadão (CC)</MenuItem>
                          <MenuItem value="trc">Autorização de residência (TRC)</MenuItem>
                          <MenuItem value="cplp">CPLP</MenuItem>
                        </TextField>
                      )}
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Cidade"
                          error={!!errors.city}
                          helperText={errors.city?.message}
                          required
                          sx={{ flex: 1, minWidth: 250 }}
                        />
                      )}
                    />
                    <Box sx={{ flex: 1 }} />
                  </Box>

                  {/* Document Type Selection */}
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: "#2c3e50", fontWeight: 600 }}
                    >
                      Tipo de Documento de Identidade
                    </Typography>
                    <Controller
                      name="idCardType"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          component="fieldset"
                          error={!!errors.idCardType}
                        >
                          <FormLabel component="legend">
                            Escolha o formato do documento:
                          </FormLabel>
                          <RadioGroup {...field} row>
                            <FormControlLabel
                              value="image"
                              control={<Radio />}
                              label="Imagens (Frente e Verso)"
                            />
                            <FormControlLabel
                              value="pdf"
                              control={<Radio />}
                              label="PDF"
                            />
                          </RadioGroup>
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* File Uploads */}
                  <Typography
                    variant="h6"
                    sx={{ color: "#2c3e50", fontWeight: 600 }}
                  >
                    Documentos Necessários
                  </Typography>

                  {/* ID Card Documents */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600, color: "#2c3e50" }}
                    >
                      Documento de Identidade
                    </Typography>
                    <Stack spacing={2}>
                      {idCardType === "image" ? (
                        <>
                          <Card variant="outlined" sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {uiPT.register.idCardFront}
                                </Typography>
                                <Chip
                                  label="Obrigatório"
                                  color="error"
                                  size="small"
                                />
                              </Box>
                              <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUploadOutlined />}
                                fullWidth
                                sx={{ justifyContent: "center" }}
                              >
                                {uiPT.resubmit.selectFile}
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={handleFileChange("idCardFront")}
                                  required={idCardType === "image"}
                                />
                              </Button>
                              {uploadedFiles.idCardFront && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 2,
                                    bgcolor: "success.lighter",
                                    borderRadius: 1,
                                  }}
                                >
                                  <CheckCircle
                                    sx={{ color: "success.main", fontSize: 20 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "success.main",
                                      fontWeight: 600,
                                      flexGrow: 1,
                                    }}
                                  >
                                    {uploadedFiles.idCardFront}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeFile("idCardFront")}
                                    sx={{ color: "error.main" }}
                                  >
                                    <Cancel sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Box>
                              )}
                              {errors.idCardFront && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ display: "block" }}
                                >
                                  {errors.idCardFront.message}
                                </Typography>
                              )}
                            </Stack>
                          </Card>

                          <Card variant="outlined" sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {uiPT.register.idCardBack}
                                </Typography>
                                <Chip
                                  label="Obrigatório"
                                  color="error"
                                  size="small"
                                />
                              </Box>
                              <Button
                                component="label"
                                variant="outlined"
                                startIcon={<CloudUploadOutlined />}
                                fullWidth
                                sx={{ justifyContent: "center" }}
                              >
                                {uiPT.resubmit.selectFile}
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={handleFileChange("idCardBack")}
                                  required={idCardType === "image"}
                                />
                              </Button>
                              {uploadedFiles.idCardBack && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 2,
                                    bgcolor: "success.lighter",
                                    borderRadius: 1,
                                  }}
                                >
                                  <CheckCircle
                                    sx={{ color: "success.main", fontSize: 20 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "success.main",
                                      fontWeight: 600,
                                      flexGrow: 1,
                                    }}
                                  >
                                    {uploadedFiles.idCardBack}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeFile("idCardBack")}
                                    sx={{ color: "error.main" }}
                                  >
                                    <Cancel sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Box>
                              )}
                              {errors.idCardBack && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ display: "block" }}
                                >
                                  {errors.idCardBack.message}
                                </Typography>
                              )}
                            </Stack>
                          </Card>
                        </>
                      ) : (
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                {uiPT.register.idCardPdf}
                              </Typography>
                              <Chip
                                label="Obrigatório"
                                color="error"
                                size="small"
                              />
                            </Box>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<CloudUploadOutlined />}
                              fullWidth
                              sx={{ justifyContent: "center" }}
                            >
                              {uiPT.resubmit.selectFile}
                              <input
                                type="file"
                                accept="application/pdf"
                                hidden
                                onChange={handleFileChange("idCardPdf")}
                                required={idCardType === "pdf"}
                              />
                            </Button>
                            {uploadedFiles.idCardPdf && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  p: 2,
                                  bgcolor: "success.lighter",
                                  borderRadius: 1,
                                }}
                              >
                                <CheckCircle
                                  sx={{ color: "success.main", fontSize: 20 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "success.main",
                                    fontWeight: 600,
                                    flexGrow: 1,
                                  }}
                                >
                                  {uploadedFiles.idCardPdf}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile("idCardPdf")}
                                  sx={{ color: "error.main" }}
                                >
                                  <Cancel sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}
                            {errors.idCardPdf && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ display: "block" }}
                              >
                                {errors.idCardPdf.message}
                              </Typography>
                            )}
                          </Stack>
                        </Card>
                      )}
                    </Stack>
                  </Box>

                  {/* Other Documents */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600, color: "#2c3e50" }}
                    >
                      Outros Documentos
                    </Typography>
                    <Stack spacing={2}>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {uiPT.register.selfie}
                            </Typography>
                            <Chip
                              label="Obrigatório"
                              color="error"
                              size="small"
                            />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: "center" }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange("selfie")}
                              required
                            />
                          </Button>
                          {uploadedFiles.selfie && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 2,
                                bgcolor: "success.lighter",
                                borderRadius: 1,
                              }}
                            >
                              <CheckCircle
                                sx={{ color: "success.main", fontSize: 20 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "success.main",
                                  fontWeight: 600,
                                  flexGrow: 1,
                                }}
                              >
                                {uploadedFiles.selfie}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("selfie")}
                                sx={{ color: "error.main" }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.selfie && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ display: "block" }}
                            >
                              {errors.selfie.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {uiPT.register.modalityPhoto}
                            </Typography>
                            <Chip
                              label="Obrigatório"
                              color="error"
                              size="small"
                            />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: "center" }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange("modalityPhoto")}
                              required
                            />
                          </Button>
                          {uploadedFiles.modalityPhoto && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                p: 2,
                                bgcolor: "success.lighter",
                                borderRadius: 1,
                              }}
                            >
                              <CheckCircle
                                sx={{ color: "success.main", fontSize: 20 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "success.main",
                                  fontWeight: 600,
                                  flexGrow: 1,
                                }}
                              >
                                {uploadedFiles.modalityPhoto}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("modalityPhoto")}
                                sx={{ color: "error.main" }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.modalityPhoto && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ display: "block" }}
                            >
                              {errors.modalityPhoto.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      {modality === "motorcycle" && (
                        <Card variant="outlined" sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600 }}
                              >
                                {uiPT.register.driverLicense}
                              </Typography>
                              <Chip
                                label="Obrigatório"
                                color="error"
                                size="small"
                              />
                            </Box>
                            <Button
                              component="label"
                              variant="outlined"
                              startIcon={<CloudUploadOutlined />}
                              fullWidth
                              sx={{ justifyContent: "center" }}
                            >
                              {uiPT.resubmit.selectFile}
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleFileChange("driverLicense")}
                                required={modality === "motorcycle"}
                              />
                            </Button>
                            {uploadedFiles.driverLicense && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  p: 2,
                                  bgcolor: "success.lighter",
                                  borderRadius: 1,
                                }}
                              >
                                <CheckCircle
                                  sx={{ color: "success.main", fontSize: 20 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "success.main",
                                    fontWeight: 600,
                                    flexGrow: 1,
                                  }}
                                >
                                  {uploadedFiles.driverLicense}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile("driverLicense")}
                                  sx={{ color: "error.main" }}
                                >
                                  <Cancel sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            )}
                            {errors.driverLicense && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ display: "block" }}
                              >
                                {errors.driverLicense.message}
                              </Typography>
                            )}
                          </Stack>
                        </Card>
                      )}

                      {/* CAE Document */}
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Código de Atividade Económica (CAE)
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Documento PDF do Código de Atividade Económica
                          </Typography>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="application/pdf"
                              hidden
                              onChange={handleFileChange("caeDocument")}
                              required
                            />
                          </Button>
                          {uploadedFiles.caeDocument && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "success.lighter", borderRadius: 1 }}>
                              <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: "success.main", fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.caeDocument}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("caeDocument")}
                                sx={{ color: "error.main" }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.caeDocument && (
                            <Typography variant="caption" color="error" sx={{ display: "block" }}>
                              {errors.caeDocument.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      {/* Tax Clearance Document */}
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Certidão de Não Dívida - Autoridade Tributária
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Documento PDF da Certidão de Não Dívida da Autoridade Tributária
                          </Typography>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="application/pdf"
                              hidden
                              onChange={handleFileChange("taxClearanceDocument")}
                              required
                            />
                          </Button>
                          {uploadedFiles.taxClearanceDocument && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "success.lighter", borderRadius: 1 }}>
                              <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: "success.main", fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.taxClearanceDocument}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("taxClearanceDocument")}
                                sx={{ color: "error.main" }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.taxClearanceDocument && (
                            <Typography variant="caption" color="error" sx={{ display: "block" }}>
                              {errors.taxClearanceDocument.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>
                    </Stack>
                  </Box>

                  {/* Terms Acceptance */}
                  <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                    <Controller
                      name="termsAccepted"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              sx={{
                                color: "#667eea",
                                "&.Mui-checked": { color: "#667eea" },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Autorizo o uso dos meus dados e documentos
                              exclusivamente para fins de criação e ativação de
                              conta na plataforma Uber.
                            </Typography>
                          }
                          sx={{ alignItems: "flex-start" }}
                        />
                      )}
                    />
                    {errors.termsAccepted && (
                      <Typography variant="caption" sx={{ color: "#FF5630"}}>
                        {errors.termsAccepted.message}
                      </Typography>
                    )}
                  </Box>

                  {/* Alerts */}
                  {error && <Alert severity="error">{error}</Alert>}

                  {success && <Alert severity="success">{success}</Alert>}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      textTransform: "none",
                    }}
                  >
                    {loading ? "Registrando..." : "Registrar via Indicação"}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
